import { promises as fs } from 'node:fs';
import * as fsSync from 'node:fs';
import path from 'node:path';
import ts from 'typescript';
import type { SourceInfo, EnhanceOptions, EnhanceResult } from './types';
import { findControllerFiles, buildSourceMap, enhanceOpenApiPaths } from './utils';

/**
 * Enhances OpenAPI JSON with source file location metadata
 * Can be called programmatically or run as a script
 */
export async function enhanceOpenApiWithSourceInfo(options: EnhanceOptions = {}): Promise<EnhanceResult> {
  const startTime = Date.now();

  const openapiPath = options.openapiPath || path.resolve(__dirname, '../client/src/api/gen/openapi.json');
  const serverDir = options.serverDir || path.resolve(__dirname, '../server');
  const writeFile = options.writeFile !== false;

  let openapi: any;
  if (options.openapiData) {
    // Use provided data (for in-memory enhancement)
    openapi = JSON.parse(JSON.stringify(options.openapiData)); // Deep clone
  } else {
    // Read from file
    const openapiContent = await fs.readFile(openapiPath, 'utf-8');
    openapi = JSON.parse(openapiContent);
  }

  const controllerFiles = await findControllerFiles(serverDir);
  const sourceMap = await buildSourceMap(controllerFiles, processControllerFile);
  const enhanced = enhanceOpenApiPaths(openapi, sourceMap);

  if (writeFile) {
    await fs.writeFile(openapiPath, JSON.stringify(openapi, null, 2) + '\n', 'utf-8');
  }

  const duration = Date.now() - startTime;

  return {
    openapi,
    stats: {
      duration,
      controllersFound: controllerFiles.length,
      endpointsExtracted: sourceMap.size,
      endpointsEnhanced: enhanced,
    },
  };
}

/**
 * Process a single controller file
 */
async function processControllerFile(filePath: string): Promise<Map<string, SourceInfo>> {
  const relativePath = path.relative(process.cwd(), filePath);

  // Parse file
  const content = await fs.readFile(filePath, 'utf-8');
  const sourceFile = ts.createSourceFile(filePath, content, ts.ScriptTarget.Latest, true);

  return extractControllerMetadata(sourceFile, relativePath);
}

/**
 * Extract controller metadata from TypeScript source file
 */
function extractControllerMetadata(sourceFile: ts.SourceFile, filePath: string): Map<string, SourceInfo> {
  const metadata = new Map<string, SourceInfo>();
  let controllerPath = '';
  let className = '';

  // Helper function to get decorators from both old and new TypeScript APIs
  function getDecorators(node: ts.Node): readonly ts.Decorator[] {
    // TypeScript 5.x: decorators are in modifiers array
    if ('modifiers' in node && Array.isArray(node.modifiers)) {
      return (node.modifiers as ts.ModifierLike[]).filter(
        (mod): mod is ts.Decorator => mod.kind === ts.SyntaxKind.Decorator,
      );
    }
    // TypeScript 4.x: decorators are in decorators array
    if ('decorators' in node && Array.isArray(node.decorators)) {
      return node.decorators as readonly ts.Decorator[];
    }
    return [];
  }

  function visit(node: ts.Node): void {
    // Extract @Controller decorator and its path
    if (ts.isClassDeclaration(node)) {
      const decorators = getDecorators(node);

      // Extract class name
      if (node.name) {
        className = node.name.getText(sourceFile);
      }

      for (const decorator of decorators) {
        if (ts.isCallExpression(decorator.expression)) {
          const expression = decorator.expression;
          const decoratorName = expression.expression.getText(sourceFile);

          if (decoratorName === 'Controller') {
            if (expression.arguments.length > 0) {
              const arg = expression.arguments[0];
              if (ts.isStringLiteral(arg)) {
                controllerPath = arg.text;
              }
            }
          }
        }
      }
    }

    // Extract methods with HTTP decorators
    if (ts.isMethodDeclaration(node) && node.name) {
      const methodName = node.name.getText(sourceFile);
      let httpMethod = '';
      let routePath = '';
      const { line } = sourceFile.getLineAndCharacterOfPosition(node.getStart(sourceFile));

      const decorators = getDecorators(node);

      for (const decorator of decorators) {
        if (ts.isCallExpression(decorator.expression)) {
          const decoratorName = decorator.expression.expression.getText(sourceFile);
          if (['Get', 'Post', 'Put', 'Delete', 'Patch', 'Options', 'Head', 'All'].includes(decoratorName)) {
            httpMethod = decoratorName.toLowerCase();
            if (decorator.expression.arguments.length > 0) {
              const arg = decorator.expression.arguments[0];
              if (ts.isStringLiteral(arg)) {
                routePath = arg.text;
              }
            }
          }
        }
      }

      if (httpMethod && methodName && className) {
        const operationId = `${className}_${methodName}`;
        metadata.set(operationId, {
          file: filePath,
          line: line + 1,
          method: httpMethod,
          controllerPath,
          routePath,
        });
      }
    }

    ts.forEachChild(node, visit);
  }

  visit(sourceFile);
  return metadata;
}

// --- API Route Parsing (synchronous, for build-time injection) ---

const HTTP_DECORATOR_NAMES = ['Get', 'Post', 'Put', 'Delete', 'Patch', 'Options', 'Head', 'All'];

function findControllerFilesSync(dir: string): string[] {
  const files: string[] = [];
  function scan(currentDir: string): void {
    let entries: fsSync.Dirent[];
    try {
      entries = fsSync.readdirSync(currentDir, { withFileTypes: true });
    } catch {
      return;
    }
    for (const entry of entries) {
      const fullPath = path.join(currentDir, entry.name);
      if (entry.isDirectory()) {
        scan(fullPath);
      } else if (entry.isFile() && entry.name.endsWith('.controller.ts')) {
        files.push(fullPath);
      }
    }
  }
  scan(dir);
  return files;
}

function normalizeApiPath(controllerPath: string, routePath: string): string {
  const combined = routePath ? `${controllerPath}/${routePath}` : controllerPath;
  let normalized = combined.replace(/\/+/g, '/');
  if (!normalized.startsWith('/')) normalized = `/${normalized}`;
  if (normalized.length > 1 && normalized.endsWith('/')) normalized = normalized.slice(0, -1);
  return normalized;
}

function parseControllerRoutes(filePath: string): Array<{ method: string; path: string }> {
  let content: string;
  try {
    content = fsSync.readFileSync(filePath, 'utf-8');
  } catch {
    return [];
  }

  const sourceFile = ts.createSourceFile(filePath, content, ts.ScriptTarget.Latest, true);
  const routes: Array<{ method: string; path: string }> = [];

  function getDecoratorsCompat(node: ts.Node): readonly ts.Decorator[] {
    if ('modifiers' in node && Array.isArray(node.modifiers)) {
      return (node.modifiers as ts.ModifierLike[]).filter(
        (mod): mod is ts.Decorator => mod.kind === ts.SyntaxKind.Decorator,
      );
    }
    if ('decorators' in node && Array.isArray(node.decorators)) {
      return node.decorators as readonly ts.Decorator[];
    }
    return [];
  }

  function getStringArg(expr: ts.CallExpression): string {
    if (expr.arguments.length > 0 && ts.isStringLiteral(expr.arguments[0])) {
      return expr.arguments[0].text;
    }
    return '';
  }

  function visit(node: ts.Node): void {
    if (ts.isClassDeclaration(node)) {
      let controllerPath = '';
      for (const dec of getDecoratorsCompat(node)) {
        if (ts.isCallExpression(dec.expression)) {
          const name = dec.expression.expression.getText(sourceFile);
          if (name === 'Controller') {
            controllerPath = getStringArg(dec.expression);
            break;
          }
        }
      }

      for (const member of node.members) {
        if (!ts.isMethodDeclaration(member)) continue;
        for (const dec of getDecoratorsCompat(member)) {
          if (!ts.isCallExpression(dec.expression)) continue;
          const decoratorName = dec.expression.expression.getText(sourceFile);
          if (!HTTP_DECORATOR_NAMES.includes(decoratorName)) continue;

          const routePath = getStringArg(dec.expression);
          const fullPath = normalizeApiPath(controllerPath, routePath);
          const method = decoratorName === 'All' ? '*' : decoratorName.toUpperCase();
          routes.push({ method, path: fullPath });
        }
      }
    }
    ts.forEachChild(node, visit);
  }

  visit(sourceFile);
  return routes;
}

/**
 * Scan serverDir for NestJS controller files and extract all parameterized API routes.
 * Synchronous — safe to call at preset config time (before bundling).
 */
export function parseApiRoutes(serverDir: string): Array<{ method: string; path: string }> {
  const resolvedDir = path.resolve(serverDir);
  const controllerFiles = findControllerFilesSync(resolvedDir);
  const routes: Array<{ method: string; path: string }> = [];
  for (const filePath of controllerFiles) {
    routes.push(...parseControllerRoutes(filePath));
  }
  return routes;
}
